import { __stringify, _keys, BadImplementationException } from '@nu-art/ts-common';
import { getUrlQuery, ModuleFE_RoutingV2, ModuleFE_WindowMessenger } from '@nu-art/thunderstorm-frontend';
import { EpicAction_CloseActivityRequest, EpicAction_CloseAppResponse, EpicAction_InitiateHandshake, EpicEvent_RequestToCloseApp, Filtered_Sources } from './consts.js';
import { avatarMode, UrlParam_EnvironmentId, UrlParam_FacilityId } from '@app/advisor-shared';
import { ModuleFE_SmartOnFHIR } from '../advisor-fhir/index.js';
import { Key_CloseActivity } from '../../ui/index.js';
import { ModuleFE_MessagesTracker } from './ModuleFE_MessagesTracker.js';
import { UserTypesToRouteKeys } from '../../consts.js';
import { ModuleFE_ActiveUser } from '../../_entity/advisor-session/ModuleFE_ActiveUser.js';
import { ModuleFE_AdvisorSession } from '../../_entity/advisor-session/index.js';
import { ModuleFE_AdvisorState } from '../advisor-state/ModuleFE_AdvisorState.js';
class ModuleFE_AdvisorWindowMessenger_Class extends ModuleFE_MessagesTracker {
    // @ts-ignore
    receiver;
    messenger = ModuleFE_WindowMessenger.createMessenger(window.parent);
    token = 'NO-TOKEN-YET';
    _launchTimeStamp;
    constructor() {
        super();
    }
    onDeployLaunchParameterAgain = () => {
        this.logInfo('Launch parameters triggered AGAIN!!!');
        this.receiver.removeProcessor('deploy-launch-parameters');
    };
    onDeployLaunchParameters = async (message) => {
        const query = getUrlQuery();
        const environmentId = query[UrlParam_EnvironmentId];
        const facilityId = query[UrlParam_FacilityId];
        this._launchTimeStamp = this.launchTimestampToEpochMs(message.data.launchTimestamp);
        if (!environmentId)
            return this.logError('Missing Environment id in url');
        if (!facilityId)
            return this.logError('Missing Facility id in url');
        const body = {
            facilityId,
            environmentId,
            launchData: message.data
        };
        try {
            const response = await ModuleFE_SmartOnFHIR.launchDeploy(body);
            ModuleFE_AdvisorState.setProperties(response.uiState);
            ModuleFE_AdvisorSession.setArtifactMetadata(response.artifactMetadata);
            ModuleFE_AdvisorSession.lastSeenAt = response.lastSeenAt;
            ModuleFE_AdvisorSession.hasRecapItems = !!response.hasRecapItems;
        }
        catch (e) {
            this.logError('Error while launching Deploy: ', e);
        }
        const routeKey = UserTypesToRouteKeys['physician'];
        const route = ModuleFE_RoutingV2.getRouteByKey(routeKey);
        if (!route)
            throw new BadImplementationException(`Route doesn't exist for routeKey: ${routeKey}`);
        this.receiver.addProcessor('deploy-launch-parameters', this.onDeployLaunchParameterAgain);
        ModuleFE_RoutingV2.goToRoute(route, query);
    };
    initializeReceiver() {
        if (this.receiver)
            return this.logError('receiver already exists');
        const transformer = data => {
            this.logInfo(`received message:`, data);
            // for now until we'll have a more conclusive transformer and better understanding of epic responses we'll patch this
            if (typeof data === 'object' && 'EventName' in data) {
                return [{ key: data.EventName, ...data }];
            }
            return _keys(data).map(_key => ({
                key: _key,
                [_key]: data[_key]
            }));
        };
        const transformMiddleware = (transform) => {
            return (data) => {
                if (typeof data === 'object' && 'encounterFhirId' in data)
                    return [{
                            key: 'deploy-launch-parameters',
                            data
                        }];
                this.addMessageToHistory({ type: 'received', content: data });
                return transform(data);
            };
        };
        this.receiver = ModuleFE_WindowMessenger
            .createReceiver(window.origin, transformMiddleware(transformer))
            .setFilter((message) => {
            //filter out some webpack messages
            if (typeof message.type === 'string' && message.type?.startsWith('webpack')) {
                this.logVerbose('filtered out webpack message: ', message);
                return false;
            }
            if (typeof message === 'string' && message.startsWith('webpack')) {
                this.logVerbose('filtered out webpack message: ', message);
                return false;
            }
            //filter out React DevTools console messages
            return !Filtered_Sources.includes(message.source);
        })
            .setDefaultProcessor(async (message) => {
            const logMessage = message;
            this.logDebug(`\n------------Warning-----------\nReceived a message from Epic, that hasn't a mapped processor.`);
            this.logDebug('Failed-to-process-object sent to be processed:');
            this.logDebug(__stringify(logMessage));
            this.addErrorMessageToHistory(logMessage);
        })
            .addProcessor('actionExecuted', message => {
            this.logInfo(`Epic Action ${message.actionExecuted ? 'Completed' : 'Failed'}`);
        })
            .addProcessor('deploy-launch-parameters', this.onDeployLaunchParameters)
            .addProcessor('token', message => {
            this.setEpicToken(message.token);
            this.logInfo(`Epic Token: ${message.token}`);
        })
            .addProcessor(EpicEvent_RequestToCloseApp, message => {
            // if epic is forcing the closure of the app there's nothing we can do
            if (message.EventArgs.ForceClose)
                return;
            if (!ModuleFE_ActiveUser.isUserActive())
                return;
            ModuleFE_ActiveUser.onApplicationTriedToClose();
            // every time we want we would like to avoid app closure
            this.sendMessage({
                token: this.token,
                action: EpicAction_CloseAppResponse,
                args: {
                    CanClose: false
                }
            });
        })
            .mount();
        this.logVerbose('receiver initialized');
    }
    //######################### Epic #########################
    initiateEpicHandshake = (args) => {
        this.logVerbose('Handshaking with epic');
        this.sendMessage({
            action: EpicAction_InitiateHandshake,
            args: args
        });
    };
    closeActivity = (args) => {
        this.logVerbose('Closing activity');
        // for epic purposes
        this.messenger.sendMessage({
            token: this.token,
            args: args,
            action: EpicAction_CloseActivityRequest
        });
        // for frame purposes
        this.sendMessage({ key: Key_CloseActivity });
    };
    setEpicToken(token) {
        this.token = token;
    }
    //######################### Avatar #########################
    postAvatarLaunched = () => {
        const providerType = ModuleFE_AdvisorSession.getUserType();
        if (providerType !== avatarMode)
            return;
        this.sendMessage({
            key: 'avatar-launched'
        });
    };
    sendMessage(message) {
        if (typeof message === 'string') {
            const saferContent = message
                .replace(/'/g, '"')
                .replace(/”/g, '"')
                .replace(/“/g, '"')
                .replace('{{TOKEN}}', this.token);
            message = JSON.parse(saferContent);
        }
        this.addMessageToHistory({ type: 'sent', content: message });
        message.token = this.token;
        this.messenger.sendMessage(message);
    }
    //######################### General #########################
    get launchTimeStamp() {
        return this._launchTimeStamp;
    }
    launchTimestampToEpochMs(launchTimestamp) {
        if (!launchTimestamp)
            return undefined;
        const epochMs = Date.parse(launchTimestamp);
        if (Number.isNaN(epochMs)) {
            this.logError(`Invalid Mayo launchTimestamp: ${launchTimestamp}`);
            return undefined;
        }
        return epochMs;
    }
}
export const ModuleFE_AdvisorWindowMessenger = new ModuleFE_AdvisorWindowMessenger_Class();
//# sourceMappingURL=ModuleFE_AdvisorWindowMessenger.js.map